import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditNotesContent } from './content'
import { EditNotesControls } from './controls'

export enum Input {
  GroomingPreferences = 'grooming_preferences',
  InternalNotes = 'internal_notes',
}

export const editNotesSchema = z.object({
  [Input.GroomingPreferences]: z.string()
    .max(2048, { message: 'Grooming preferences must be less than 2048 characters.' })
    .optional(),
  [Input.InternalNotes]: z.string()
    .max(2048, { message: 'Internal notes must be less than 2048 characters.' })
    .optional(),
})

export type EditNotesSchema = z.infer<typeof editNotesSchema>

type EditNotesFormContextType = {
  onSubmit: (values: EditNotesSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditNotesFormContext = createContext<EditNotesFormContextType | undefined>(undefined)

type EditNotesFormProps = EditNotesFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditNotesSchema>,
}

export function EditNotesForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditNotesFormProps) {

  const context: EditNotesFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditNotesFormContext.Provider value={context}>
      <Form<EditNotesSchema, typeof editNotesSchema>
        schema={editNotesSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditNotesFormContext.Provider>
  )
}

export function useEditNotesForm() {
  const context = useContext(EditNotesFormContext)
  if (context === undefined) {
    throw new Error('useEditNotesForm must be used within an EditNotesFormProvider')
  }
  return context
}

// Compound components
EditNotesForm.Content = EditNotesContent
EditNotesForm.Controls = EditNotesControls
